import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, DollarSign, Tag, Receipt, FileText, TrendingUp, Pencil, Trash2, ChevronDown, ChevronUp, Eye,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { expensesAPI } from '../../services/api';
import { format } from 'date-fns';
import { EmptyState, KpiChip, cn } from '../../components/ui';
import { resolveUploadUrl } from '../../utils/uploadUrl';

const now = new Date();
const currentMonth = now.getMonth() + 1;
const currentYear = now.getFullYear();

export default function ExpenseList() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listRes, catRes, sumRes] = await Promise.all([
        expensesAPI.list({
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          category_id: categoryId || undefined,
          payment_mode: paymentMode || undefined,
        }),
        expensesAPI.getCategories(),
        expensesAPI.getSummary(currentMonth, currentYear),
      ]);
      setList(listRes.data || []);
      setCategories(catRes.data || []);
      setSummary(sumRes.data || { total: 0 });
    } catch (e) {
      console.error(e);
      setList([]);
      setCategories([]);
      setSummary({ total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, categoryId, paymentMode]);

  const categoryTotals = useMemo(() => {
    const map = new Map();
    list.forEach((e) => {
      const key = e.category_name || 'Uncategorised';
      map.set(key, (map.get(key) || 0) + Number(e.amount || 0));
    });
    return [...map.entries()]
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
  }, [list]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete expense of ₹${Number(row.amount).toLocaleString()} (${row.category_name || 'uncategorised'})?`)) {
      return;
    }
    setDeletingId(row.id);
    try {
      await expensesAPI.delete(row.id);
      await fetchData();
    } catch (e) {
      alert(e?.message || 'Failed to delete expense');
    } finally {
      setDeletingId(null);
    }
  };

  const filterCount = [startDate, endDate, categoryId, paymentMode].filter(Boolean).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <DollarSign className="h-7 w-7 text-ga-primary" />
              Expense Management
            </h1>
            <p className="text-ga-text-muted mt-1">Track ground expenses</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/dashboard/expenses/analytics"
              className="ga-btn-ghost inline-flex items-center gap-2"
            >
              <TrendingUp className="h-5 w-5" />
              Analytics
            </Link>
            <Link
              to="/dashboard/expenses/new"
              className="ga-btn-primary inline-flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Expense
            </Link>
          </div>
        </div>

        {/* Compact KPIs — total always visible; top categories on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiChip
            label="Total (this month)"
            value={`₹${Number(summary.total || 0).toLocaleString()}`}
            icon={DollarSign}
            accent="primary"
          />
          {categoryTotals.map((c) => (
            <div key={c.name} className="hidden sm:block">
              <KpiChip
                label={c.name}
                value={`₹${c.total.toLocaleString()}`}
                icon={Tag}
                accent="info"
              />
            </div>
          ))}
          {categoryTotals.length === 0 && (
            <div className="hidden sm:block sm:col-span-3">
              <KpiChip
                label="Filtered list total"
                value={`₹${list.reduce((s, e) => s + Number(e.amount || 0), 0).toLocaleString()}`}
                icon={Tag}
                accent="info"
              />
            </div>
          )}
        </div>

        <div className="ga-card">
          {/* Mobile: collapse filters behind a toggle so the list stays above the fold */}
          <div className="mb-4 flex items-center justify-between gap-2 sm:hidden">
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border border-ga-border-subtle px-3 py-2 text-sm font-medium text-ga-text-primary"
            >
              Filters{filterCount > 0 ? ` (${filterCount})` : ''}
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {categoryId && (
              <button
                type="button"
                onClick={() => setCategoryId('')}
                className="text-xs font-medium text-ga-primary"
              >
                Clear category
              </button>
            )}
          </div>

          <div className={cn('space-y-4 mb-4', !showFilters && 'hidden sm:block')}>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategoryId('')}
                className={cn(
                  'rounded-ga-full border px-3.5 py-1.5 text-sm font-semibold transition-colors',
                  categoryId === ''
                    ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                    : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary'
                )}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoryId(String(c.id))}
                  className={cn(
                    'rounded-ga-full border px-3.5 py-1.5 text-sm font-semibold transition-colors',
                    String(categoryId) === String(c.id)
                      ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                      : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary'
                  )}
                >
                  {c.category_name}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <div className="flex flex-col">
                <label htmlFor="expense-from-date" className="text-xs font-medium text-ga-text-muted mb-1">
                  From
                </label>
                <input
                  id="expense-from-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="ga-input w-full sm:w-40 py-2"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="expense-to-date" className="text-xs font-medium text-ga-text-muted mb-1">
                  To
                </label>
                <input
                  id="expense-to-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="ga-input w-full sm:w-40 py-2"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="expense-payment-mode" className="text-xs font-medium text-ga-text-muted mb-1">
                  Payment mode
                </label>
                <select
                  id="expense-payment-mode"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="ga-input w-full sm:w-36 py-2"
                >
                  <option value="">All modes</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="BANK">Bank</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-ga-text-muted">Loading...</div>
          ) : list.length === 0 ? (
            <EmptyState
              card
              icon={FileText}
              title="No expenses found"
              description="Try changing the filters, or add your first expense."
              action={(
                <Link to="/dashboard/expenses/new" className="ga-btn-primary inline-flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Expense
                </Link>
              )}
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ga-border-subtle text-left text-ga-text-muted">
                      <th className="p-3 font-medium">Date</th>
                      <th className="p-3 font-medium">Category</th>
                      <th className="p-3 font-medium">Description</th>
                      <th className="p-3 font-medium text-right">Amount</th>
                      <th className="p-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => (
                      <tr key={row.id} className="border-b border-ga-border-subtle/50 hover:bg-ga-bg-surface-subtle/50">
                        <td className="p-3 text-ga-text-primary whitespace-nowrap">
                          {row.expense_date ? format(new Date(row.expense_date), 'dd MMM yyyy') : '—'}
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1.5 rounded-ga-full bg-ga-primary-soft px-2.5 py-0.5 text-xs font-semibold text-ga-primary">
                            {row.category_name || 'Uncategorised'}
                          </span>
                        </td>
                        <td className="p-3 text-ga-text-muted max-w-xs truncate">
                          {row.description || (
                            <span className="text-ga-text-subtle">
                              {row.payment_mode}{row.paid_to ? ` · ${row.paid_to}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right font-semibold text-ga-text-primary whitespace-nowrap">
                          ₹{Number(row.amount).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/dashboard/expenses/${row.id}`}
                              className="p-2 rounded-lg text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-primary"
                              aria-label="View expense details"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                            {row.invoice_url && (
                              <a
                                href={resolveUploadUrl(row.invoice_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-primary"
                                aria-label="Open receipt"
                              >
                                <Receipt className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/expenses/${row.id}/edit`)}
                              className="p-2 rounded-lg text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-primary"
                              aria-label="Edit expense"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              disabled={deletingId === row.id}
                              className="p-2 rounded-lg text-ga-text-muted hover:bg-ga-danger/10 hover:text-ga-danger disabled:opacity-50"
                              aria-label="Delete expense"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile compact rows */}
              <div className="md:hidden space-y-2">
                {list.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center gap-3 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface px-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-ga-text-subtle">
                          {row.expense_date ? format(new Date(row.expense_date), 'dd MMM') : '—'}
                        </span>
                        <span className="truncate text-xs font-semibold text-ga-primary">
                          {row.category_name || 'Uncategorised'}
                        </span>
                      </div>
                      {row.description ? (
                        <p className="mt-0.5 truncate text-sm text-ga-text-primary">{row.description}</p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-sm font-bold text-ga-text-primary">
                      ₹{Number(row.amount).toLocaleString()}
                    </span>
                    <div className="flex shrink-0 items-center">
                      <Link
                        to={`/dashboard/expenses/${row.id}`}
                        className="p-2 rounded-lg text-ga-text-muted hover:text-ga-primary"
                        aria-label="View expense details"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/expenses/${row.id}/edit`)}
                        className="p-2 rounded-lg text-ga-text-muted hover:text-ga-primary"
                        aria-label="Edit expense"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.id}
                        className="p-2 rounded-lg text-ga-text-muted hover:text-ga-danger disabled:opacity-50"
                        aria-label="Delete expense"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
